/**
 * KG pipeline service client.
 *
 * Now routed through backend wrapper endpoints that handle auth and workspace resolution.
 * Backend endpoints proxy to SGM service:
 *   POST   /api/v1/workspaces/{workspace_id}/kg/sgm/pipeline/trigger
 *   GET    /api/v1/workspaces/{workspace_id}/kg/sgm/pipeline/runs/{run_id}
 *   GET    /api/v1/workspaces/{workspace_id}/kg/pipeline-runs?limit=1  (newest run)
 *
 * Toggle: REACT_APP_PIPELINE_API=mock → offline simulated runs.
 */

import { isMockApiFlag } from "../apiMode";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { apiService, API_ENDPOINTS } from "../api";
import { kgWritePath } from "../kg/kgWritePath";
import { getApiErrorMessage } from "../apiErrors";
import type {
  PipelineRun,
  PipelineRunListItem,
  PipelineRunListResponse,
  TriggerPipelineRequest,
  TriggerPipelineResponse,
} from "./pipeline.types";
import {
  mockFetchLatestPipelineRun,
  mockFetchPipelineRun,
  mockTriggerPipeline,
} from "./pipelineApi.mock";
import {
  PIPELINE_UNEXPECTED_RESPONSE_MESSAGE,
  toPipelineErrorMessage,
} from "./pipelineMappers";

const isMockPipelineApiEnabled = () =>
  isMockApiFlag(process.env.REACT_APP_PIPELINE_API);

/* Raw status/body stay out of the UI — surface them for debugging in dev only. */
const logPipelineApiError = (status: number | null, detail: string) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(`Pipeline API error (status ${status ?? "network"})`, detail);
  }
};

/* The service sometimes answers 200 with an error body — never trust shape on status alone. */
const ensureExpectedShape = <T>(
  payload: T,
  requiredField: keyof T & string,
): T => {
  if (!payload || !payload[requiredField]) {
    logPipelineApiError(200, JSON.stringify(payload));
    throw new Error(PIPELINE_UNEXPECTED_RESPONSE_MESSAGE);
  }
  return payload;
};

export const triggerPipeline = async (): Promise<TriggerPipelineResponse> => {
  const request: TriggerPipelineRequest = { trigger: "manual" };

  if (isMockPipelineApiEnabled()) {
    return mockTriggerPipeline(request);
  }

  const workspaceId = getActiveWorkspaceId();

  let data: TriggerPipelineResponse;
  try {
    data = await apiService.post<TriggerPipelineResponse>(
      await kgWritePath(API_ENDPOINTS.kg.sgmPipelineTrigger(workspaceId)),
      request,
    );
  } catch (error) {
    const status = (error as any)?.response?.status ?? null;
    const detail = error instanceof Error ? error.message : "";
    logPipelineApiError(status, detail);
    throw new Error(toPipelineErrorMessage(status));
  }
  // Outside the try: a malformed 200 must not be re-labelled as a network error.
  return ensureExpectedShape(data, "run_id");
};

export const fetchPipelineRun = async (runId: string): Promise<PipelineRun> => {
  if (isMockPipelineApiEnabled()) {
    return mockFetchPipelineRun(runId);
  }

  const workspaceId = getActiveWorkspaceId();

  let data: PipelineRun;
  try {
    data = await apiService.get<PipelineRun>(
      API_ENDPOINTS.kg.sgmPipelineRunStatus(workspaceId, runId),
    );
  } catch (error) {
    const status = (error as any)?.response?.status ?? null;
    const detail = error instanceof Error ? error.message : "";
    logPipelineApiError(status, detail);
    throw new Error(toPipelineErrorMessage(status));
  }
  return ensureExpectedShape(data, "status");
};

/** Fallback when the backend gives no usable reason for a failed status read. */
export const LATEST_PIPELINE_RUN_ERROR_MESSAGE =
  "Couldn't load when the knowledge graph was last updated.";

/**
 * The workspace's newest graph build, or null when it has never had one.
 * Builds start on their own when data changes; the canvas polls this to show their
 * status and to refresh when one finishes.
 */
export const fetchLatestPipelineRun = async (
  workspaceId: string,
): Promise<PipelineRunListItem | null> => {
  let data: PipelineRunListResponse;
  try {
    data = isMockPipelineApiEnabled()
      ? await mockFetchLatestPipelineRun(workspaceId)
      : await apiService.get<PipelineRunListResponse>(
          API_ENDPOINTS.kg.pipelineRuns(workspaceId),
          { limit: 1 },
        );
  } catch (error) {
    const status = (error as any)?.response?.status ?? null;
    logPipelineApiError(status, error instanceof Error ? error.message : "");
    throw new Error(getApiErrorMessage(error, LATEST_PIPELINE_RUN_ERROR_MESSAGE));
  }
  if (!data || !Array.isArray(data.items)) {
    logPipelineApiError(200, JSON.stringify(data));
    throw new Error(PIPELINE_UNEXPECTED_RESPONSE_MESSAGE);
  }
  return data.items[0] ?? null;
};
